import { urlComSessao } from "./sessao";
/**
 * Cliente da API deste sistema.
 *
 * Toda chamada leva o header `x-sercamp-usuario` — é assim que o servidor sabe
 * quem está do outro lado (mesmo padrão dos outros sistemas do portal).
 */
const BASE = "/api";

let sessionUser = "";
export function setSessionUser(u: string) {
  sessionUser = u ?? "";
}
// Lê a sessão já no carregamento do módulo: sem isso, um F5 dispara as
// primeiras chamadas com o header vazio e o servidor responde 401 (foi
// exatamente o bug do painel de auditoria do Portal).
try {
  sessionUser = localStorage.getItem("sercamp-desenvolvedor-user") ?? "";
} catch {
  /* storage bloqueado — segue sem sessão */
}

const authHeaders = () => ({ "x-sercamp-usuario": sessionUser });
const jsonHeaders = () => ({ "content-type": "application/json", ...authHeaders() });

export class ApiError extends Error {
  status: number;
  constructor(message: string, status = 500) {
    super(message);
    this.status = status;
  }
}

async function handle(res: Response) {
  if (!res.ok) {
    let msg = "Erro " + res.status;
    try {
      const body = await res.json();
      if (body?.error) msg = body.error;
    } catch {
      /* resposta sem JSON — fica a mensagem padrão */
    }
    throw new ApiError(msg, res.status);
  }
  if (res.status === 204) return null;
  return res.json();
}

/** Monta a query string ignorando o que está vazio ou em "todos". */
const qs = (filtros: Record<string, string>) => {
  const p = new URLSearchParams(
    Object.entries(filtros).filter(([, v]) => v && v !== "todos") as [string, string][],
  ).toString();
  return p ? "?" + p : "";
};

const get = (url: string) => fetch(BASE + url, { headers: authHeaders() }).then(handle);
const post = (url: string, data?: unknown) =>
  fetch(BASE + url, { method: "POST", headers: jsonHeaders(), body: JSON.stringify(data ?? {}) }).then(handle);
const put = (url: string, data: unknown) =>
  fetch(BASE + url, { method: "PUT", headers: jsonHeaders(), body: JSON.stringify(data) }).then(handle);
const del = (url: string) =>
  fetch(BASE + url, { method: "DELETE", headers: authHeaders() }).then(handle);

export const api = {
  me: () => get("/me"),
  painel: () => get("/painel"),

  solicitacoes: {
    listar: (filtros: Record<string, string> = {}) => get("/solicitacoes" + qs(filtros)),
    /** O endereço do CSV (quem baixa é o navegador — ver auditoria.urlExportar). */
    urlExportar: (filtros: Record<string, string> = {}) =>
      urlComSessao(BASE + "/solicitacoes/exportar" + qs(filtros)),
    obter: (id: number) => get("/solicitacoes/" + id),
    criar: (dados: Record<string, unknown>) => post("/solicitacoes", dados),
    atualizar: (id: number, dados: Record<string, unknown>) => put("/solicitacoes/" + id, dados),
    remover: (id: number) => del("/solicitacoes/" + id),
    comentar: (id: number, texto: string) => post("/solicitacoes/" + id + "/comentarios", { texto }),
  },

  reunioes: {
    listar: () => get("/reunioes"),
    criar: (dados: Record<string, unknown>) => post("/reunioes", dados),
    atualizar: (id: number, dados: Record<string, unknown>) => put("/reunioes/" + id, dados),
    remover: (id: number) => del("/reunioes/" + id),
    /** Pedido vindo de um sistema: aceitar vira reunião marcada (com lembrete). */
    aceitar: (id: number, dados: Record<string, unknown> = {}) => post("/reunioes/" + id + "/aceitar", dados),
    /** Recusar exige o motivo — é o que a pessoa lê no sistema dela. */
    recusar: (id: number, motivo: string) => post("/reunioes/" + id + "/recusar", { motivo }),
  },

  /** Renovações: o que vence (domínio, certificado, licença…). */
  renovacoes: {
    listar: () => get("/renovacoes"),
    criar: (dados: Record<string, unknown>) => post("/renovacoes", dados),
    atualizar: (id: number, dados: Record<string, unknown>) => put("/renovacoes/" + id, dados),
    remover: (id: number) => del("/renovacoes/" + id),
    /** "já renovei": empurra o vencimento para o próximo período. */
    renovar: (id: number, venceEm?: string) =>
      post("/renovacoes/" + id + "/renovar", venceEm ? { vence_em: venceEm } : {}),
  },

  /** Cópia de segurança: bancos, documentos e configuração (aba Backup). */
  backup: {
    config: () => get("/backup/config"),
    salvar: (dados: Record<string, unknown>) => put("/backup/config", dados),
    /** O que entra na próxima cópia (bancos e pastas encontrados agora). */
    alvos: () => get("/backup/alvos"),
    listar: (limite = 60) => get("/backup?limite=" + limite),
    rodarAgora: () => post("/backup/rodar"),
    remover: (id: number) => del("/backup/" + id),
    /**
     * O endereço do download. Não é `fetch`: quem salva o arquivo é o
     * NAVEGADOR (mesmo motivo do CSV da auditoria) — e a cópia inteira pode
     * ter centenas de MB, que não faz sentido passar pela memória do app.
     * O `u=` vai na URL porque o navegador não manda o header da sessão numa
     * navegação comum (ver exigirAcessoDeDownload em server/backup.ts).
     */
    urlBaixar: (id: number, parte?: string) =>
      urlComSessao(BASE + "/backup/" + id + "/baixar" + (parte ? "?parte=" + encodeURIComponent(parte) : "")),
  },

  /** Envio de e-mail: o que a aba Lembretes configura (e testa). */
  email: {
    config: () => get("/email/config"),
    salvar: (dados: Record<string, unknown>) => put("/email/config", dados),
    testar: (para?: string) => post("/email/testar", para ? { para } : {}),
  },

  push: {
    chave: () => get("/push/chave"),
    inscrever: (dados: Record<string, unknown>) => post("/push/inscrever", dados),
    cancelar: (endpoint: string) => post("/push/cancelar", { endpoint }),
    testar: () => post("/push/testar"),
  },

  lembretes: {
    listar: () => get("/lembretes"),
    criar: (dados: Record<string, unknown>) => post("/lembretes", dados),
    atualizar: (id: number, dados: Record<string, unknown>) => put("/lembretes/" + id, dados),
    remover: (id: number) => del("/lembretes/" + id),
    enviar: (id: number) => post("/lembretes/" + id + "/enviar"),
  },

  atualizacoes: {
    listar: () => get("/atualizacoes"),
    criar: (dados: Record<string, unknown>) => post("/atualizacoes", dados),
    atualizar: (id: number, dados: Record<string, unknown>) => put("/atualizacoes/" + id, dados),
    remover: (id: number) => del("/atualizacoes/" + id),
    avisar: (id: number, dados: Record<string, unknown>) => post("/atualizacoes/" + id + "/avisar", dados),
    desavisar: (id: number) => post("/atualizacoes/" + id + "/desavisar"),
  },

  auditoria: {
    listar: (filtros: Record<string, string> = {}) => get("/auditoria" + qs(filtros)),
    resumo: () => get("/auditoria/resumo"),
    alertas: (sistema?: string) =>
      get("/auditoria/alertas" + qs(sistema ? { sistema } : {})),
    /** O retrato completo de um sistema (para a aba "Relatório do sistema"). */
    doSistema: (sistema: string, dias = 30) =>
      get("/auditoria/sistema/" + encodeURIComponent(sistema) + "?dias=" + dias),
    /** O dossiê de uma pessoa: tudo o que ela fez, em todos os sistemas. */
    doUsuario: (usuario: string, dias = 30) =>
      get("/auditoria/usuario/" + encodeURIComponent(usuario) + "?dias=" + dias),
    /**
     * O endereço do CSV. Não é `fetch`: o download precisa acontecer no
     * NAVEGADOR (é ele que salva o arquivo com o nome que o servidor manda).
     * Por isso a tela abre esta URL numa aba, em vez de baixar por JavaScript.
     */
    urlExportar: (filtros: Record<string, string> = {}) => urlComSessao(BASE + "/auditoria/exportar" + qs(filtros)),
  },

  /** Monitor: os sistemas ao vivo (o fluxo em si abre com abrirFluxoMonitor, no fim deste arquivo). */
  monitor: {
    resumo: () => get("/monitor/resumo"),
    /** Linhas mais antigas, direto do arquivo de log do sistema. */
    historico: (sistema: string, linhas = 300) =>
      get("/monitor/historico?sistema=" + encodeURIComponent(sistema) + "&linhas=" + linhas),
  },

  agenda: {
    config: () => get("/agenda/config"),
    salvarConfig: (dados: Record<string, unknown>) => put("/agenda/config", dados),
    novoLink: () => post("/agenda/config/novo-link"),
    criarFaixa: (dados: Record<string, unknown>) => post("/agenda/disponibilidade", dados),
    removerFaixa: (id: number) => del("/agenda/disponibilidade/" + id),
    criarBloqueio: (dados: Record<string, unknown>) => post("/agenda/bloqueios", dados),
    removerBloqueio: (id: number) => del("/agenda/bloqueios/" + id),
  },
};

/**
 * O FLUXO DO MONITOR (SSE). Não passa pelo `handle` porque a resposta não
 * termina: quem lê o corpo, bloco a bloco, é a tela (MonitorView.tsx). Vai
 * com `fetch` — e não `EventSource` — porque o EventSource não manda
 * cabeçalho, e é no cabeçalho que vai a sessão assinada (src/sessao.ts).
 */
export const abrirFluxoMonitor = (signal?: AbortSignal) =>
  fetch(BASE + "/monitor/stream", { headers: { ...authHeaders(), accept: "text/event-stream" }, signal });
